import { Card, Col, Empty, List, Row, Typography } from 'antd';
import {
  useGetArticleDetailsQuery,
  useSearchArticlesQuery,
} from '@/redux/articles';
import { formatters } from '@/core';
import styles from './ArticlesOverviewPanel.module.scss';
import { NavLink } from 'react-router-dom';
import { ArticleStatus } from '@/core/http.articles';
import { RichTextValue, useDefaultTablePaging } from '@/components';
import { useMemo, useState } from 'react';

const { Title, Text, Paragraph } = Typography;

export interface ArticlesOverviewPanelProps {
  searchString?: string;
}

export function ArticlesOverviewPanel(props: ArticlesOverviewPanelProps) {
  const { searchString } = props;
  const { setTotal, paging, params } = useDefaultTablePaging();
  const [recentId, setRecentId] = useState<string>();
  const { data: paged, isFetching } = useSearchArticlesQuery({
    statuses: [ArticleStatus.Published],
    paging: params,
    searchString: searchString,
  });

  const isFiltered = useMemo(
    () => !!searchString && searchString.length > 0,
    [searchString],
  );

  const { data: recentArticle } = useGetArticleDetailsQuery(
    { id: recentId! },
    { skip: !recentId || isFiltered },
  );

  const articles = useMemo(() => {
    if (paged) {
      setTotal(paged.total);
      setRecentId(paged.items[0]?.id);
    }
    return paged?.items;
  }, [paged, setTotal]);

  if (!articles?.length || isFetching) {
    return <Empty />;
  }

  const [main, ...rest] = articles;

  return (
    <>
      <Row>
        {!isFiltered && (
          <Col span={16}>
            <Card className={styles.main}>
              <NavLink to={`/help/${main.id}`}>
                <Title level={2}>{main.title}</Title>
              </NavLink>

              <Text type="secondary">
                {formatters.dateTime(main.updatedAt)}
              </Text>

              {main.shortDescription && (
                <Paragraph ellipsis={{ rows: 3 }} className="mt-2">
                  {main.shortDescription}
                </Paragraph>
              )}
              <RichTextValue
                label={false}
                value={recentArticle?.description}
                ownerId={main.id}
              />
            </Card>
          </Col>
        )}
        <Col span={isFiltered ? 24 : 7} offset={isFiltered ? 0 : 1}>
          <List
            dataSource={isFiltered ? articles : rest}
            pagination={{ ...paging, pageSize: 5 }}
            className={styles.list}
            renderItem={(article, index) => (
              <List.Item className={index !== 0 ? 'mt-3' : undefined}>
                <Card
                  title={
                    <NavLink to={`/help/${article.id}`}>
                      <Title level={3}>{article.title}</Title>
                    </NavLink>
                  }
                  className={styles.card}
                >
                  <Text type="secondary">
                    {formatters.dateTime(article.updatedAt)}
                  </Text>

                  {article.shortDescription && (
                    <Paragraph ellipsis={{ rows: 3 }}>
                      {article.shortDescription}
                    </Paragraph>
                  )}
                </Card>
              </List.Item>
            )}
          ></List>
        </Col>
      </Row>
    </>
  );
}
